import React from 'react';
import P from '@hero/ui-kit/typography/P';
import H from '@hero/ui-kit/typography/H';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import useComponentMargin from '@hero/ui-kit/hooks/useComponentMargin';
import { useTransferToD2C } from '../context/TransferToD2CContext';

interface SuccessReportProps {
    onClose: () => void;
}

const SuccessReport: React.FC<SuccessReportProps> = ({ onClose }) => {
    const marginTop = useComponentMargin({ marginTop: 'large' });
    const { selectedPlan, selectedMitigation, rtmPlanOffers } = useTransferToD2C();

    const userMessage = React.useMemo(() => {
        if (selectedPlan) {
            const plan = rtmPlanOffers?.find((p) => p.storefront_key === selectedPlan);
            if (plan) {
                const monthlyPrice = `$${(plan.monthly_fee / 100).toFixed(2)}`;
                const commitment =
                    plan.commitment_months > 0
                        ? `${plan.commitment_months}-month commitment`
                        : 'No commitment';
                return `${plan.name} - ${monthlyPrice}/month (${commitment})`;
            }
            return 'Plan not supported';
        }

        if (selectedMitigation) {
            const months = selectedMitigation.months || 2;
            const mitigationMessages: Record<string, string> = {
                'months-free': 'with free month/s of membership',
                'discount-subscription-50': `with 50% discount for the next ${months} months`,
                'mitigation-50': `with 50% discount for the next ${months} months`,
                'discount-subscription-67': `with almost 70% discount for the next ${months} months`,
                'mitigation-67': `with almost 70% discount for the next ${months} months`,
                'mitigation-22': `with 22% discount for the next ${months} months`,
                'mitigation-33': `with 33% discount for the next ${months} months`,
                'mitigation-78': `with 77.8% discount for the next ${months} months`,
                'pause-subscription': `with pause for the next ${months} months`
            };

            return mitigationMessages[selectedMitigation.hardship] || 'Mitigation not supported';
        }

        return 'Plan not supported';
    }, [selectedPlan, selectedMitigation, rtmPlanOffers]);

    return (
        <>
            <Section noDefaultPadding paddingHorizontal="large">
                <H role="h5">Tell the member:</H>
                <P size="large">
                    <strong>Thank you</strong> for staying with Hero!
                </P>
                <P noDefaultMargin>We have updated your subscription:</P>
                <P size="large" strong>
                    {userMessage}
                </P>
                <P size="large">
                    We're always here to help -{' '}
                    <strong>improving your health and well-being</strong> is our goal.
                </P>
            </Section>
            <Section noDefaultPadding centered styles={marginTop}>
                <Button width="large" onClick={onClose}>
                    Ok
                </Button>
            </Section>
        </>
    );
};

export default SuccessReport;
